<template>
  <header
    id="header"
    class="relative top-0 z-40 flex h-[72px] w-full flex-row items-center justify-between bg-bg-dark px-3 text-white sm:fixed lg:px-10"
  >
    <NuxtLink class="relative z-10" to="/">
      <ClientOnly>
        <template v-if="brandingStore.loaded">
          <img
            v-if="brandingStore.getHeaderLogoUrl"
            :src="brandingStore.getHeaderLogoUrl"
            alt="logo"
            class="hidden h-12 w-auto object-contain lg:block"
          />
          <Logo v-else class="hidden h-auto w-[100px] lg:block" />
          <img
            v-if="brandingStore.getHeaderLogoUrl"
            :src="brandingStore.getHeaderLogoUrl"
            alt="logo"
            class="h-10 w-auto object-contain lg:hidden"
          />
          <LogoMobile v-else class="h-auto w-10 lg:hidden" />
        </template>
        <template v-else>
          <div aria-hidden="true" class="hidden h-12 w-[100px] lg:block" />
          <div aria-hidden="true" class="size-10 lg:hidden" />
        </template>
      </ClientOnly>
    </NuxtLink>

    <nav
      v-if="authStore.getIsLoggedIn"
      class="absolute inset-x-0 mx-auto hidden flex-row items-center justify-center gap-9 md:flex"
    >
      <NuxtLink
        v-for="item in navItems"
        :key="item.icon"
        :to="item.path"
        class="flex flex-row gap-4 no-underline transition-all duration-500"
        :class="{
          'rounded-xl bg-white/10 px-6 py-3': item.routesIncluded.find((r) => route.name?.toString() === r),
        }"
      >
        <span class="relative">
          <UiIcon :name="item.icon"></UiIcon>
        </span>

        <h5 v-if="item.routesIncluded.find((r) => route.name?.toString() === r)">{{ item.name }}</h5>
      </NuxtLink>
    </nav>
    <div v-if="authStore.getIsLoggedIn" class="z-10 flex flex-row items-center gap-5">
      <HeaderUserMenu />
    </div>
  </header>
</template>

<script setup lang="ts">
import { PERMISSIONS } from '~/constants'
import Logo from '@/assets/images/logo.svg?component'
import LogoMobile from '@/assets/images/logo-mobile.svg?component'

const authStore = useAuthStore()
const brandingStore = useBrandingStore()

const route = useRoute()

const propertyPath = computed(() => {
  if (useHasPermissions([PERMISSIONS.CAN_VIEW_PROPERTIES])) return '/properties/all'
  if (useHasPermissions([PERMISSIONS.CAN_VIEW_EXCLUSIVE_UNITS])) return '/properties/exclusives'
  if (useHasPermissions([PERMISSIONS.CAN_VIEW_DICTIONARIES])) return '/properties/dictionaries/developers'
  if (useHasPermissions([PERMISSIONS.CAN_VIEW_INTERNAL_PROJECTS])) return '/properties/internal-projects'
  return '/properties/all'
})

const adminSettingsPath = computed(() => {
  if (useHasPermissions([PERMISSIONS.CAN_MANAGE_USERS])) return '/admin-settings/users'
  if (useHasPermissions([PERMISSIONS.CAN_MANAGE_ROLES])) return '/admin-settings/permissions'
  if (useHasPermissions([PERMISSIONS.CAN_MANAGE_PROPERTIES])) return '/admin-settings/tags'
  if (useHasPermissions([PERMISSIONS.CAN_MANAGE_EXCLUSIVE_UNITS])) return '/admin-settings/unit-settings'
  return '/admin-settings/users'
})

const allNavItems = computed(() => [
  {
    icon: 'home',
    name: 'Property',
    path: propertyPath.value,
    routesIncluded: [
      'properties',
      'properties-all',
      'properties-exclusives',
      'property-id',
      'properties-dictionaries-developers',
      'properties-dictionaries-amenities',
      'internal-projects',
    ],
    permissions: [
      PERMISSIONS.CAN_VIEW_PROPERTIES,
      PERMISSIONS.CAN_VIEW_EXCLUSIVE_UNITS,
      PERMISSIONS.CAN_VIEW_DICTIONARIES,
      PERMISSIONS.CAN_VIEW_INTERNAL_PROJECTS,
    ],
  },
  {
    icon: 'settings',
    name: 'Admin settings',
    path: adminSettingsPath.value,
    routesIncluded: [
      'admin-settings',
      'admin-settings-users',
      'admin-settings-permissions',
      'admin-settings-user-permissions',
      'admin-settings-tags',
      'admin-settings-unit-settings',
      'admin-settings-tags-settings',
    ],
    permissions: [
      PERMISSIONS.CAN_MANAGE_USERS,
      PERMISSIONS.CAN_MANAGE_ROLES,
      PERMISSIONS.CAN_MANAGE_PROPERTIES,
      PERMISSIONS.CAN_MANAGE_EXCLUSIVE_UNITS,
    ],
  },
  {
    icon: 'folder',
    name: 'Content management',
    path: '/content-management/news-feed',
    routesIncluded: [
      'content-management',
      'content-management-news-feed',
      'content-management-marketing-materials',
      'content-management-useful-materials',
      'content-management-user-manuals',
    ],
    permissions: [],
  },
])

const navItems = computed(() => allNavItems.value.filter((item) => useHasPermissions(item.permissions)))
</script>
